import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Text, Loader, Button } from '@pingux/astro';
import ContentCopyIcon from '@pingux/mdi-react/ContentCopyIcon';
import CheckIcon from '@pingux/mdi-react/CheckIcon';
import { Credential } from '@app-types/tap-partner-vault';
import StatusBadge from '@components/StatusBadge';
import { credentialsService } from '@services/credentials';
import { useAuthContext } from 'state/context/AuthContext';
import { isGroupOwnersGroup } from 'utils/auth';

interface CredentialListProps {
  credentials: Credential[];
  loading?: boolean;
  onValidate?: (id: string) => void;
  onDelete?: (id: string) => void;
}

const secretRequestIndicatorStyles = {
  pending: {
    label: 'Request pending',
    backgroundColor: '#fef3c7',
    color: '#92400e',
  },
  approved: {
    label: 'Access approved',
    backgroundColor: '#d1fae5',
    color: '#065f46',
  },
  denied: {
    label: 'Request denied',
    backgroundColor: '#fee2e2',
    color: '#991b1b',
  },
} as const;

const getSecretRequestIndicator = (status: Credential['secret_request_status']) =>
  status ? secretRequestIndicatorStyles[status] : null;

const CredentialList: React.FC<CredentialListProps> = ({
  credentials,
  loading = false,
  onValidate,
  onDelete,
}) => {
  const navigate = useNavigate();
  const {
    state: { user },
  } = useAuthContext();
  const userRole = user?.role ?? 'user';

  const [secrets, setSecrets] = useState<Record<string, string>>({});
  const [revealedIds, setRevealedIds] = useState<Set<string>>(new Set());
  const [loadingIds, setLoadingIds] = useState<Set<string>>(new Set());
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [sortConfig, setSortConfig] = useState<{ column: string; direction: 'asc' | 'desc' } | null>({ column: 'tenant', direction: 'asc' });
  const [collapsedPartners, setCollapsedPartners] = useState<Set<string>>(new Set());

  const handleSort = useCallback((column: string) => {
    setSortConfig(prev =>
      prev?.column === column
        ? { column, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { column, direction: 'asc' }
    );
  }, []);

  const togglePartner = useCallback((partner: string) => {
    setCollapsedPartners(prev => {
      const next = new Set(prev);
      if (next.has(partner)) next.delete(partner);
      else next.add(partner);
      return next;
    });
  }, []);

  // Sort credentials within each group
  const sortedCredentials = [...credentials].sort((a, b) => {
    if (!sortConfig) return 0;
    const dir = sortConfig.direction === 'asc' ? 1 : -1;
    switch (sortConfig.column) {
      case 'tenant':
        return a.tenant_id.localeCompare(b.tenant_id) * dir;
      case 'account': {
        const aVal = a.credential_type === 'api_key' ? 'API Key - No Account' : (a.account_name ?? '');
        const bVal = b.credential_type === 'api_key' ? 'API Key - No Account' : (b.account_name ?? '');
        return aVal.localeCompare(bVal) * dir;
      }
      case 'status':
        return a.validation_status.localeCompare(b.validation_status) * dir;
      default:
        return 0;
    }
  });

  // Build groups: partners always sorted A→Z
  const partnerNames = credentials
    .map(c => c.partner)
    .filter((p, i, arr) => arr.indexOf(p) === i)
    .sort((a, b) => a.localeCompare(b));
  const groups = partnerNames.map(partner => ({
    partner,
    items: sortedCredentials.filter(c => c.partner === partner),
  }));

  const allExpanded = partnerNames.length > 0 && partnerNames.every(p => !collapsedPartners.has(p));

  const toggleAll = useCallback(() => {
    setCollapsedPartners(prev => {
      const next = new Set(prev);
      partnerNames.forEach(partner => {
        if (allExpanded) next.add(partner);
        else next.delete(partner);
      });
      return next;
    });
  }, [allExpanded, partnerNames]);

  const handleRowClick = useCallback((id: string) => {
    navigate(`/credentials/${id}`);
  }, [navigate]);

  const handleReveal = useCallback(async (credential: Credential) => {
    const { id } = credential;

    if (revealedIds.has(id)) {
      setRevealedIds(prev => { const next = new Set(prev); next.delete(id); return next; });
      return;
    }

    if (!secrets[id]) {
      setLoadingIds(prev => new Set(prev).add(id));
      try {
        const data = await credentialsService.getSecret(id);
        setSecrets(prev => ({ ...prev, [id]: data.secret }));
      } catch {
        return;
      } finally {
        setLoadingIds(prev => { const next = new Set(prev); next.delete(id); return next; });
      }
    }

    setRevealedIds(prev => new Set(prev).add(id));
  }, [revealedIds, secrets]);

  const handleCopy = useCallback(async (id: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      // clipboard unavailable
    }
  }, []);

  const tableHeaderStyle = {
    fontWeight: '600',
    color: '#374151',
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    padding: '0.75rem 1rem',
    borderBottom: '2px solid #e5e7eb',
    backgroundColor: '#f9fafb',
    textAlign: 'left' as const,
  };

  const tableCellStyle = {
    padding: '1rem',
    borderBottom: '1px solid #e5e7eb',
    fontSize: '0.875rem',
    color: '#111827',
    verticalAlign: 'middle',
  };

  const typeHeaderStyle = {
    ...tableHeaderStyle,
    paddingRight: '0.5rem',
  };

  const statusHeaderStyle = {
    ...tableHeaderStyle,
    paddingLeft: '0.5rem',
    paddingRight: '2.25rem',
  };

  const typeCellStyle = {
    ...tableCellStyle,
    paddingRight: '0.5rem',
  };

  const statusCellStyle = {
    ...tableCellStyle,
    paddingLeft: '0.5rem',
    paddingRight: '2.25rem',
  };

  const tableRowStyle = {
    cursor: 'pointer',
    transition: 'background-color 0.15s ease',
  };

  const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse' as const,
    backgroundColor: 'white',
    borderRadius: '0.5rem',
    overflow: 'hidden',
    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
        <Loader />
      </Box>
    );
  }

  if (credentials.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', padding: '3rem', color: '#6b7280' }}>
        <Text variant="bodyStrong" sx={{ marginBottom: '0.5rem' }}>No credentials found</Text>
        <Text variant="body">Add your first credential to get started.</Text>
      </Box>
    );
  }

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.5rem' }}>
        <button
          onClick={toggleAll}
          style={{
            fontSize: '0.75rem',
            color: '#6b7280',
            background: 'none',
            border: '1px solid #d1d5db',
            borderRadius: '0.25rem',
            padding: '0.2rem 0.6rem',
            cursor: 'pointer',
          }}
        >
          {allExpanded ? 'Collapse All' : 'Expand All'}
        </button>
      </div>
      <table style={tableStyle}>
        <thead>
          <tr>
            {(['tenant', 'account'] as const).map(col => (
              <th
                key={col}
                style={{ ...tableHeaderStyle, cursor: 'pointer', userSelect: 'none' }}
                onClick={() => handleSort(col)}
              >
                {col.charAt(0).toUpperCase() + col.slice(1)}
                {' '}
                {sortConfig?.column === col ? (sortConfig.direction === 'asc' ? '▲' : '▼') : <span style={{ opacity: 0.3 }}>▲</span>}
              </th>
            ))}
            <th style={tableHeaderStyle}>Secret</th>
            <th
              style={{ ...statusHeaderStyle, cursor: 'pointer', userSelect: 'none' }}
              onClick={() => handleSort('status')}
            >
              Status
              {' '}
              {sortConfig?.column === 'status' ? (sortConfig.direction === 'asc' ? '▲' : '▼') : <span style={{ opacity: 0.3 }}>▲</span>}
            </th>
          </tr>
        </thead>
        <tbody>
          {groups.map(({ partner, items }) => {
            const isExpanded = !collapsedPartners.has(partner);
            return (
              <React.Fragment key={`group-${partner}`}>
                {/* Partner group header */}
                <tr
                  onClick={() => togglePartner(partner)}
                  style={{ cursor: 'pointer', backgroundColor: '#f3f4f6', userSelect: 'none' }}
                >
                  <td colSpan={4} style={{ padding: '0.65rem 1rem', borderBottom: '1px solid #e5e7eb' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span style={{ fontSize: '0.7rem', color: '#6b7280' }}>{isExpanded ? '▼' : '▶'}</span>
                      <Text variant="bodyStrong" sx={{ fontSize: '0.875rem', color: '#111827' }}>{partner}</Text>
                      <span style={{
                        fontSize: '0.75rem',
                        color: '#6b7280',
                        backgroundColor: '#e5e7eb',
                        borderRadius: '9999px',
                        padding: '0.1rem 0.5rem',
                      }}>
                        {items.length}
                      </span>
                    </div>
                  </td>
                </tr>

                {/* Credential rows */}
                {isExpanded && items.map((credential) => {
                  const isAdmin = userRole === 'admin' || userRole === 'superadmin';
                  const canReveal = isAdmin;
                  const isRevealed = revealedIds.has(credential.id);
                  const isLoadingSecret = loadingIds.has(credential.id);
                  const secret = secrets[credential.id];
                  const secretRequestIndicator = !isAdmin
                    ? getSecretRequestIndicator(credential.secret_request_status)
                    : null;

                  return (
                    <tr
                      key={credential.id}
                      style={tableRowStyle}
                      onClick={() => handleRowClick(credential.id)}
                    >
                      <td style={tableCellStyle}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                          <Text sx={{ fontFamily: 'monospace' }}>{credential.tenant_id}</Text>
                          {credential.is_private && (
                            <span style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              padding: '0.125rem 0.4rem',
                              borderRadius: '9999px',
                              fontSize: '0.65rem',
                              fontWeight: '600',
                              backgroundColor: '#ede9fe',
                              color: '#6d28d9',
                              letterSpacing: '0.05em',
                              textTransform: 'uppercase',
                              whiteSpace: 'nowrap',
                            }}>
                              Private
                            </span>
                          )}
                          {credential.group_tags.filter((tag) => !isGroupOwnersGroup(tag)).map((tag) => (
                            <span
                              key={`${credential.id}-${tag}`}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                padding: '0.125rem 0.4rem',
                                borderRadius: '9999px',
                                fontSize: '0.65rem',
                                fontWeight: '600',
                                backgroundColor: '#dbeafe',
                                color: '#1d4ed8',
                                letterSpacing: '0.03em',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td style={tableCellStyle}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <Text>{credential.credential_type === 'api_key' ? 'API Key - No Account' : (credential.account_name ?? '—')}</Text>
                          {credential.account_name && credential.credential_type !== 'api_key' && (
                            <span onClick={e => e.stopPropagation()}>
                              <Button
                                variant="link"
                                onPress={() => handleCopy(credential.id + '_account', credential.account_name!)}
                                sx={{ color: copiedId === credential.id + '_account' ? '#059669' : '#6b7280', padding: '0' }}
                              >
                                {copiedId === credential.id + '_account' ? <CheckIcon size={14} /> : <ContentCopyIcon size={14} />}
                              </Button>
                            </span>
                          )}
                        </div>
                      </td>
                      <td
                        style={{ ...tableCellStyle, whiteSpace: 'normal', wordBreak: 'break-all', maxWidth: '300px' }}
                        onClick={canReveal ? e => e.stopPropagation() : undefined}
                      >
                        {canReveal ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <Text sx={{ fontFamily: 'monospace', color: isRevealed ? '#111827' : '#9ca3af' }}>
                              {isRevealed && secret ? secret : '••••••••••••'}
                            </Text>
                            {isRevealed && secret && (
                              <Button
                                variant="link"
                                onPress={() => handleCopy(credential.id, secret)}
                                sx={{ color: copiedId === credential.id ? '#059669' : '#6b7280', padding: '0' }}
                              >
                                {copiedId === credential.id ? <CheckIcon size={14} /> : <ContentCopyIcon size={14} />}
                              </Button>
                            )}
                            <Button
                              variant="link"
                              onPress={() => handleReveal(credential)}
                              isDisabled={isLoadingSecret}
                              sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}
                            >
                              {isLoadingSecret ? '...' : isRevealed ? 'Hide' : 'Reveal'}
                            </Button>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                            <Text sx={{ color: '#9ca3af', fontFamily: 'monospace' }}>••••••••••••</Text>
                            {secretRequestIndicator && (
                              <span
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  padding: '0.125rem 0.45rem',
                                  borderRadius: '9999px',
                                  fontSize: '0.68rem',
                                  fontWeight: '600',
                                  backgroundColor: secretRequestIndicator.backgroundColor,
                                  color: secretRequestIndicator.color,
                                  letterSpacing: '0.02em',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {secretRequestIndicator.label}
                              </span>
                            )}
                          </div>
                        )}
                      </td>
                      <td style={statusCellStyle}>
                        <StatusBadge status={credential.validation_status} />
                      </td>
                    </tr>
                  );
                })}
              </React.Fragment>            );
          })}
        </tbody>
      </table>
    </Box>
  );
};

export default CredentialList;
